import React, { useState } from 'react';
import { Gift } from 'lucide-react';
import { formatEther } from 'viem';
import { colors, spacing, typography, borders, shadows, transitions } from '../../../../tokens';
import { formatTokenAmount } from '../utils/formatters';
import type { EnhancedContractStats, UserDashboard } from '../hooks/useContractReads';
import { Card } from '../../../../atoms';

interface UserStatisticsProps {
  farBalance: bigint | undefined;
  totalPendingRewards: bigint | undefined;
  userClaimedRewards: bigint | undefined;
  userDashboard: UserDashboard | undefined;
  enhancedStats: EnhancedContractStats | undefined;
  onClaimRewards: () => void;
  isClaimDisabled: boolean;
}

export const UserStatistics: React.FC<UserStatisticsProps> = ({
  farBalance,
  totalPendingRewards,
  userClaimedRewards,
  userDashboard,
  enhancedStats,
  onClaimRewards,
  isClaimDisabled,
}) => {
  // Hover state for Claim button
  const [isClaimHovered, setIsClaimHovered] = useState(false);

  // Calculate weight-based APY
  const calculateUserAPY = (): string => {
    if (!enhancedStats?.contractEmissionRate ||
        !enhancedStats?.contractTotalWeight ||
        !userDashboard?.userTotalWeight ||
        !userDashboard?.userTotalStaked) {
      return '0.00';
    }

    // Weight-based APY calculation
    const userWeight = Number(formatEther(userDashboard.userTotalWeight));
    const totalWeight = Number(formatEther(enhancedStats.contractTotalWeight));
    const userShare = userWeight / totalWeight;

    // Calculate annual emission
    const secondsPerYear = 31536000; // 365 * 24 * 60 * 60
    const annualEmission = Number(formatEther(enhancedStats.contractEmissionRate)) * secondsPerYear;

    // Your annual rewards based on your weight share
    const userAnnualRewards = annualEmission * userShare;

    // Your APY = (your annual rewards / your staked) * 100
    const userStaked = Number(formatEther(userDashboard.userTotalStaked));
    const userAPY = (userAnnualRewards / userStaked) * 100;

    return userAPY.toFixed(2);
  };

  return (
    <div>
      <style>{`
        @media (max-width: 768px) {
          .user-stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1200px) {
          .user-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
      <h2 style={{
        fontSize: typography.fontSize['2xl'],
        fontWeight: typography.fontWeight.extrabold,
        fontFamily: typography.fontFamily.heading,
        color: colors.text.primary.default,
        margin: `0 0 ${spacing.lg} 0`,
        letterSpacing: typography.letterSpacing.wide,
        textTransform: 'uppercase' as const,
      }}>
        Your Statistics
      </h2>
      <Card padding="2xl">
        <div className="user-stats-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: spacing.lg,
        }}>
          {/* Available Balance */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Available Balance
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.text.primary.default,
            }}>
              {farBalance ? formatTokenAmount(farBalance) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              FAR tokens
            </div>
          </div>

          {/* Your Total Staked */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Your Total Staked
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.purple.base,
            }}>
              {userDashboard?.userTotalStaked ? formatTokenAmount(userDashboard.userTotalStaked) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              FAR tokens
            </div>
          </div>

          {/* Pending Rewards */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Pending Rewards
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.semantic.success,
            }}>
              {totalPendingRewards ? formatTokenAmount(totalPendingRewards) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              FAR tokens
            </div>
          </div>

          {/* Active Stakes */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Active Stakes
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.cyan.base,
            }}>
              {userDashboard?.userPositionCount !== undefined ? userDashboard.userPositionCount.toString() : '0'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              Total positions
            </div>
          </div>

          {/* Your Staking Weight */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Your Staking Weight
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.purple.base,
            }}>
              {userDashboard?.userTotalWeight ? formatTokenAmount(userDashboard.userTotalWeight) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              {enhancedStats?.contractTotalWeight && userDashboard?.userTotalWeight ?
                `${((Number(formatEther(userDashboard.userTotalWeight)) / Number(formatEther(enhancedStats.contractTotalWeight))) * 100).toFixed(2)}% of total`
                : 'Pool share'}
            </div>
          </div>

          {/* Your Current APY */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Your Current APY
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.cyan.base,
            }}>
              {calculateUserAPY()}%
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              Based on your staking weight
            </div>
          </div>

          {/* Your Claimed Rewards */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Your Claimed Rewards
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.semantic.success,
            }}>
              {userClaimedRewards ? formatTokenAmount(userClaimedRewards) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              FAR tokens claimed
            </div>
          </div>

          {/* Available Reward Pool */}
          <div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.xs,
            }}>
              Available Reward Pool
            </div>
            <div style={{
              fontSize: typography.fontSize['2xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.yellow.base,
            }}>
              {enhancedStats?.contractAvailableRewards ? formatTokenAmount(enhancedStats.contractAvailableRewards) : '0.00'}
            </div>
            <div style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}>
              FAR tokens in pool
            </div>
          </div>
        </div>

        {/* Claim Rewards Button */}
        <div style={{ marginTop: spacing.lg }}>
          <button
            onClick={onClaimRewards}
            disabled={isClaimDisabled}
            onMouseEnter={() => setIsClaimHovered(true)}
            onMouseLeave={() => setIsClaimHovered(false)}
            style={{
              width: '100%',
              padding: spacing.lg,
              backgroundColor: isClaimDisabled
                ? colors.semantic.success + '40'  // Green with transparency for disabled
                : colors.semantic.success,
              color: colors.neutral.white,
              border: `1px solid ${colors.semantic.success}`,
              borderRadius: borders.component.button.radius,
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              cursor: isClaimDisabled ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              boxShadow: (!isClaimDisabled && isClaimHovered)
                ? `${shadows.glow.green.lg}, ${shadows.elevation.medium}`
                : shadows.glow.green.md,
              transform: (!isClaimDisabled && isClaimHovered)
                ? 'translateY(-2px) scale(1.02)'
                : 'translateY(0) scale(1)',
              transition: transitions.button,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Gift size={20} />
            Claim All Rewards ({totalPendingRewards ? formatTokenAmount(totalPendingRewards) : '0.00'} FAR)
          </button>
        </div>
      </Card>
    </div>
  );
};
